
/**
 * @typedef {import('@drama/contracts/drama').BundleT} Bundle
 * @typedef {import('@drama/contracts/drama').ScriptLineT} ScriptLine
 */

/**
 * @typedef {object} StudioDrama 载入态：对齐 / 标注 / 剧本 / bundle 的当前快照。
 * @property {string} drama_id
 * @property {string} [title]
 * @property {object} [align] 角色分件对齐结果（部件→骨骼命名映射）
 * @property {object} [annotations] 场景标注（景深层 / 站位点 / 遮挡区）
 * @property {ScriptLine[]} [script] 当前剧本编排
 * @property {Bundle} [bundle] 最近一次出包结果（可能为空）
 */

/**
 * studio 域：创作台专属端点（对齐保存 / 标注保存 / 去白底 / 剧本存取 / 出包 / AI 起稿）。
 *
 * 全部「重活」（去白底、出包、AI 起稿）只发请求给 server，前端不含任何算法分支——
 * 本模块只做 method/path/body 转发，与 drama/feed 等域同构。
 *
 * @param {import('../transport.js').createTransport extends (...a:any)=>infer R ? R : any} transport
 */
export function createStudioApi(transport) {
  return {
    /**
     * 我的作品列表（创作者只见自己的）。后端 `GET /api/studio/dramas` → `{ dramas: [...] }`。
     * @returns {Promise<{ dramas: Array<{ drama_id: string, title?: string, publish_status?: string }> }>}
     */
    loadDramas() {
      return transport.request('GET', '/api/studio/dramas')
    },

    /**
     * 载入作品（对齐 / 标注 / 剧本 / bundle 态）。
     * @param {string} dramaId
     * @returns {Promise<StudioDrama>}
     */
    loadDrama(dramaId) {
      return transport.request('GET', `/api/studio/drama/${encodeURIComponent(dramaId)}`)
    },

    /**
     * 角色分件对齐结果保存。`align` 为部件→骨骼命名映射（对齐 @drama/rig 骨架）。
     * @param {string} dramaId
     * @param {object} align
     * @returns {Promise<any>}
     */
    saveAlign(dramaId, align) {
      return transport.request('POST', '/api/studio/character/align', {
        body: { drama_id: dramaId, align },
      })
    },

    /**
     * 场景标注保存（景深层 / 可站位点 / 遮挡区）。
     * @param {string} dramaId
     * @param {object} annotations
     * @returns {Promise<any>}
     */
    saveAnnotations(dramaId, annotations) {
      return transport.request('POST', '/api/studio/scene/annotate', {
        body: { drama_id: dramaId, annotations },
      })
    },

    /**
     * 上传场景背景到 OSS，并写入 annotations.scenes[scene_id]。
     * @param {{ drama_id:string, scene_id:string, name?:string, src:string, world?:{w:number,h:number} }} req
     */
    uploadSceneBackground(req) {
      return transport.request('POST', '/api/studio/scene/background', { body: req })
    },

    /**
     * 上传音效/BGM 到 OSS，并写入 annotations.sfx。
     * @param {{ drama_id:string, name?:string, src:string }} req
     */
    uploadDramaSfx(req) {
      return transport.request('POST', '/api/studio/drama/sfx', { body: req })
    },

    /**
     * 保存作品角色池。角色页/导戏页“加角色”写 drama.cast。
     * @param {string} dramaId
     * @param {any[]} cast
     * @returns {Promise<any>}
     */
    saveCast(dramaId, cast) {
      return transport.request('POST', '/api/studio/drama/cast', {
        body: { drama_id: dramaId, cast },
      })
    },

    /**
     * 删除作品角色池里的一个角色（同步清理 drama.cast / drama.align / 角色链接表）。
     * @param {{ drama_id:string, character_id:string }} req
     * @returns {Promise<any>}
     */
    removeCast(req) {
      return transport.request('POST', '/api/studio/drama/cast/remove', { body: req })
    },

    /**
     * 把当前角色替换成另一个角色（同步删旧、写新）。
     * @param {{ drama_id:string, from_character_id:string, character_id:string, alias:string, template_skin?:any, asset_id?:string|null }} req
     * @returns {Promise<any>}
     */
    replaceCast(req) {
      return transport.request('POST', '/api/studio/drama/cast/replace', { body: req })
    },

    /**
     * 创建剧本角色池角色：复制模板 skin，写入 drama.align + drama.cast。
     * @param {{ drama_id:string, character_id:string, alias:string, template_id?:string }} req
     * @returns {Promise<any>}
     */
    createCharacter(req) {
      return transport.request('POST', '/api/studio/drama/character', { body: req })
    },

    /** 全局角色资产列表。 */
    listCharacterAssets() {
      return transport.request('GET', '/api/studio/character-assets')
    },

    /** 全局表情包资产列表。 */
    listExpressionPacks() {
      return transport.request('GET', '/api/studio/expression-packs')
    },

    /**
     * 上传/更新表情包某个情绪的一帧。
     * @param {{ id:string, name?:string, talk_fps?:number, emotion:string, frame:'static'|'talk', src:string, dewhite?:boolean }} req
     */
    uploadExpressionPackFrame(req) {
      return transport.request('POST', `/api/studio/expression-packs/${encodeURIComponent(req.id)}/frame`, { body: req })
    },

    /**
     * 创建全局角色资产。
     * @param {{ id?:string, name:string, description?:string, template_skin?:any }} req
     */
    createCharacterAsset(req) {
      return transport.request('POST', '/api/studio/character-assets', { body: req })
    },

    /**
     * 保存全局角色资产 skin。
     * @param {string} id
     * @param {any} skin
     */
    saveCharacterAssetSkin(id, skin) {
      return transport.request('PUT', `/api/studio/character-assets/${encodeURIComponent(id)}/skin`, { body: { skin } })
    },

    /**
     * 上传/去白底某个资产部件。
     * @param {{ id:string, part:string, src:string, dewhite?:boolean }} req
     */
    uploadCharacterAssetPart(req) {
      return transport.request('POST', `/api/studio/character-assets/${encodeURIComponent(req.id)}/part`, {
        body: { part: req.part, src: req.src, dewhite: req.dewhite },
      })
    },

    /**
     * 加人去白底（→ server → @drama/image）。前端不含去白底算法、不建 image 厂商连接。
     * @param {{ drama_id: string, character_id: string, scene_id: string, src: string, part?: string }} req
     * @returns {Promise<{ url: string }>}
     */
    cutout(req) {
      return transport.request('POST', '/api/studio/image/cutout', { body: req })
    },

    /**
     * 剧本编排数据保存（PUT 全量）。`script` 必须是合法 ScriptLine[]（调用方先过 contracts 校验）。
     * @param {string} dramaId
     * @param {ScriptLine[]} script
     * @returns {Promise<any>}
     */
    saveScript(dramaId, script) {
      return transport.request('PUT', `/api/studio/drama/${encodeURIComponent(dramaId)}/script`, {
        body: { script },
      })
    },

    /**
     * 取「实时预览数据」：后端按当前编辑态把工程组装成播放器可消费的 bundle 形状并**直接返回**
     * （不落库、不上 OSS——这是预览，不是发布出包）。返回原始 bundle，不过 motion-comic 契约裁剪：
     * 编齐角色/场景的剧组装成「富 rig bundle」（含 characters/scene/cast），是 motion-comic Bundle
     * 的超集，parseBundle 会把这些字段裁掉导致预览空白；预览 <DramaPlayer> 自行按形状渲染。
     * @param {string} dramaId
     * @returns {Promise<any>}
     */
    previewBundle(dramaId) {
      return transport.request('POST', `/api/studio/drama/${encodeURIComponent(dramaId)}/bundle`)
    },

    /**
     * 发布（OPS-PLAN P1）：编译工程→持久化 published_bundle→镜像 OSS→标记剧集 published。
     * 发布后 C 端播放器播这份真产物。返回 { ok, version, bundle_url, published_at, lines }。
     * @param {string} dramaId
     */
    publish(dramaId) {
      return transport.request('POST', `/api/studio/drama/${encodeURIComponent(dramaId)}/publish`)
    },

    /**
     * 一键导出完整内容产物。
     * 成功应返回可直接落盘的下载入口；若后端未能生成可下载目标，则返回 ok:false 与 artifactError。
     * @param {string} dramaId
     */
    exportConcatenatedVideo(dramaId) {
      return transport.request('POST', `/api/studio/drama/${encodeURIComponent(dramaId)}/export-concatenated`)
    },

    /**
     * 新建空剧集草稿（不走 LLM）。返回新 drama_id 和基础元信息。
     * @param {{ title?: string, genre?: string, locale?: string, synopsis?: string, cover?: string }} req
     * @returns {Promise<{ drama_id: string, title?: string }>}
     */
    createDrama(req) {
      return transport.request('POST', '/api/studio/dramas', { body: req })
    },

    /**
     * 修改首页剧名。
     * @param {{ drama_id: string, title: string }} req
     */
    renameDrama(req) {
      return transport.request('PATCH', `/api/studio/dramas/${encodeURIComponent(req.drama_id)}/title`, {
        body: req,
      })
    },

    /**
     * AI 起稿（→ server → @drama/dramagen）。返回 drama 草稿（角色 / 场景 / 剧本骨架）。
     * @param {{ prompt: string, genre?: string, locale?: string }} req
     * @returns {Promise<StudioDrama>}
     */
    dramagen(req) {
      return transport.request('POST', '/api/studio/dramagen', { body: req })
    },

    // ── 剧集 = 第一类编辑单元（导戏台改集优先）────────────────────────────────────
    /**
     * 集列表（编辑态）+ 场景库 + 角色池。导戏台据此建集列表 / 场景选择 / 班底增删。
     * @param {string} dramaId
     * @returns {Promise<{ episodes: Array<{no:number,title:string,scene_id:string|null,cast_count:number,line_count:number,publish_status:string}>, scenes: Array<{id:string,name:string}>, roster: any[] }>}
     */
    listEpisodes(dramaId) {
      return transport.request('GET', `/api/studio/drama/${encodeURIComponent(dramaId)}/episodes`)
    },

    /**
     * 全局场景库：汇总所有可编辑剧上传过的场景图，供“选择已有场景”使用。
     * @returns {Promise<{ scenes: Array<{ drama_id: string, drama_title: string, scene_id: string, scene_name: string, background: string, world?: {w:number,h:number}|null, person_scale?: number|null, camera?: any|null }> }>}
     */
    listSceneLibrary() {
      return transport.request('GET', '/api/studio/scenes')
    },

    /**
     * 取某集（编辑态）：可编辑字段（title/scene_id/cast/script）+ 渲染用 bundle（OSS 已解析）。
     * @param {string} dramaId @param {number} no
     */
    getEpisode(dramaId, no) {
      return transport.request('GET', `/api/studio/drama/${encodeURIComponent(dramaId)}/episodes/${no}`)
    },

    /**
     * 新增一集（追加末尾）。返回 { ok, id, no }。
     * @param {string} dramaId @param {{ title?:string, scene_id?:string|null, cast?:any[], script?:any[] }} [init]
     */
    createEpisode(dramaId, init = {}) {
      return transport.request('POST', `/api/studio/drama/${encodeURIComponent(dramaId)}/episodes`, { body: init })
    },

    /**
     * 创建某集的独立副本（复制场景、班底、剧本；副本从草稿开始）。
     * @param {string} dramaId @param {number} no
     */
    duplicateEpisode(dramaId, no) {
      return transport.request('POST', `/api/studio/drama/${encodeURIComponent(dramaId)}/episodes/${no}/duplicate`)
    },

    /**
     * 保存某集草稿（title/scene_id/cast/script 任意子集）。
     * @param {string} dramaId @param {number} no
     * @param {{ title?:string, scene_id?:string|null, cast?:any[], script?:any[] }} patch
     */
    saveEpisode(dramaId, no, patch) {
      return transport.request('PUT', `/api/studio/drama/${encodeURIComponent(dramaId)}/episodes/${no}`, { body: patch })
    },

    /** 删除某集（后续集号顺移补齐）。@param {string} dramaId @param {number} no */
    deleteEpisode(dramaId, no) {
      return transport.request('DELETE', `/api/studio/drama/${encodeURIComponent(dramaId)}/episodes/${no}`)
    },

    /** 上/下移某集。@param {string} dramaId @param {number} no @param {-1|1} dir */
    moveEpisode(dramaId, no, dir) {
      return transport.request('POST', `/api/studio/drama/${encodeURIComponent(dramaId)}/episodes/${no}/move`, { body: { dir } })
    },
  }
}
